// @flow

import React, {useCallback} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/styles';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
} from '@material-ui/core';

import {colors} from '../../../../../../assets/styles/colors';
import PremiumFeatureBadge from '../../../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import SizeGuideItem from './SizeGuideItem';
import {useIsFeatureInTier} from '../../../../../../utils/component';
import {getImageUrl} from '../../../../../../utils/image_provider';
import {useProductFormContext} from '../../../../context/ProductForm';

import type {RootState} from '../../../../..';
import type {Theme} from '@material-ui/core';

const useStyles = makeStyles((theme: Theme) => ({
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(0, 3, 3, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2, 2, 2),
    },
    background: 'white',
  },
  sectionTitle: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
    },
  },
  subtitle: {
    color: colors.grey900,
    fontWeight: 500,
    fontSize: 14,
  },
  sectionDescription: {
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  sectionCheckbox: {
    '& .MuiFormControlLabel-label': {
      color: colors.grey700,
      fontSize: '0.875rem',
    },
  },
  infoText: {
    color: colors.grey900,
    fontSize: 14,
  },
  wrapSeoExample: {
    pointerEvents: 'none',
    background: '#FFFFFF',
    border: '1px solid #E8E8ED',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    borderRadius: 8,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(1),
  },
  colorBlue: {
    color: '#2E24A3',
    fontSize: 14,
    fontWeight: 500,
  },
  colorGreen: {
    fontSize: 12,
    fontWeight: 500,
    color: '#005B24',
  },
  colorGray: {
    fontSize: 12,
    fontWeight: 500,
    color: '#717190',
  },
  disableSection: {
    pointerEvents: 'none',
    opacity: 0.5,
  },
  control: {
    margin: theme.spacing(1.5, 0),
    [theme.breakpoints.up('md')]: {
      margin: 0,
    },
  },
  gridContainer: {
    margin: theme.spacing(1, 0),
  },
  previewChart: {
    maxWidth: '100%',
  },
  linkText: {
    color: colors.primary,
    fontSize: 14,
    fontWeight: 500,
  },
  createSizeGuide: {
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '.25rem',
  },
}));

const sizeChartsSelector = (state: RootState) =>
  state.productPage.productPageSetting &&
  state.productPage.productPageSetting.storeSizeChartSetting &&
  state.productPage.productPageSetting.storeSizeChartSetting.sizeCharts;

export default function SizeGuideSection() {
  const {
    sizeGuideSelected,
    setSizeGuideSelected,
    enabledSizeGuide,
    setEnabledSizeGuide,
  } = useProductFormContext();

  const sizeCharts = useSelector(sizeChartsSelector);

  const hasSizeGuideFeature = useIsFeatureInTier('PRODUCT_SIZE_CHART');

  const classes = useStyles();

  const handleEnableSizeGuide = () => {
    setEnabledSizeGuide(ps => !ps);
  };

  const handleSelectSizeGuide = useCallback(
    (id: string) => {
      setSizeGuideSelected(id);
    },
    [setSizeGuideSelected]
  );

  const sizeChartPreview =
    sizeCharts && sizeCharts.length > 0 && !!sizeGuideSelected
      ? sizeCharts.find(sc => sc.id === sizeGuideSelected)
      : undefined;

  return (
    <section className={classes.contentSection}>
      <Typography className={classes.sectionTitle}>
        Guía de tallas {!hasSizeGuideFeature && <PremiumFeatureBadge />}
      </Typography>
      <div className={classes.control}>
        <FormControlLabel
          control={
            <Checkbox
              disabled={
                !hasSizeGuideFeature || !(sizeCharts && sizeCharts.length > 0)
              }
              checked={enabledSizeGuide}
              onChange={handleEnableSizeGuide}
              name="checkedI"
              color="primary"
            />
          }
          className={classes.sectionCheckbox}
          label="Agrega una guía de tallas o tamaños para ayudar a tus compradores a elegir mejor producto y evitar devoluciones"
        />
        {(!sizeCharts || sizeCharts.length === 0) && (
          <div className={classes.createSizeGuide}>
            <Typography className={classes.infoText}>
              No tienes guías de tallas creadas
            </Typography>

            <a
              href="/admin/my-store/pages/product-page"
              className={classes.linkText}
              target="_blank"
              rel="noopener noreferrer"
            >
              Crear guía de tallas
            </a>
          </div>
        )}
      </div>
      {enabledSizeGuide && (
        <>
          <Grid container spacing={2} className={classes.gridContainer}>
            {sizeCharts &&
              sizeCharts.length > 0 &&
              sizeCharts.map(sc => (
                <Grid key={sc.id} item xs={12} md={6}>
                  <SizeGuideItem
                    sizeChart={sc}
                    handleSelect={handleSelectSizeGuide}
                    selected={sizeGuideSelected === sc.id}
                  />
                </Grid>
              ))}
          </Grid>

          {!!sizeChartPreview && (
            <>
              <Divider className={classes.divider} />
              <Typography className={classes.subtitle}>
                Previsualización
              </Typography>

              <Grid container spacing={2} className={classes.gridContainer}>
                {sizeChartPreview.image1Id && (
                  <Grid item xs={12} md={8}>
                    <img
                      className={classes.previewChart}
                      src={getImageUrl(sizeChartPreview.image1Id)}
                      alt="size chart preview"
                    />
                  </Grid>
                )}
                {sizeChartPreview.image2Id && (
                  <Grid item xs={12} md={8}>
                    <img
                      className={classes.previewChart}
                      src={getImageUrl(sizeChartPreview.image2Id)}
                      alt="size chart preview"
                    />
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </>
      )}
    </section>
  );
}
